import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Coins } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export default function AdminPricing() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/config/pricing").then(({ data }) => setCfg(data)).catch(() => setCfg(false));
  }, []);

  if (cfg === null) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></div>;
  if (cfg === false) return <p className="text-sm" style={{ color: "var(--text-2)" }}>Could not load pricing.</p>;

  const sym = cfg.currencies?.[cfg.default_currency] || "\u20b1";

  const setTier = (i, patch) =>
    setCfg((c) => ({ ...c, tiers: c.tiers.map((t, idx) => (idx === i ? { ...t, ...patch } : t)) }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/pricing", {
        default_currency: cfg.default_currency,
        event_fee: Number(cfg.event_fee) || 0,
        tiers: cfg.tiers.map((t) => ({
          id: t.id, name: t.name, price: Number(t.price) || 0, unit: t.unit,
          desc: t.desc || "", features: t.features || [],
        })),
      });
      setCfg(data);
      toast.success("Pricing updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const fieldClass = "w-full rounded-lg bg-surface glow-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-lime-400";

  return (
    <div className="space-y-5" data-testid="admin-pricing">
      <div className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }}>
        <label className="text-xs uppercase tracking-wider mono flex items-center gap-2 mb-2" style={{ color: "var(--text-2)" }}>
          <Coins className="w-4 h-4 text-primary-c" /> Default Currency (fallback: PHP ₱)
        </label>
        <select data-testid="pricing-currency" value={cfg.default_currency}
          onChange={(e) => setCfg((c) => ({ ...c, default_currency: e.target.value }))}
          className={fieldClass} style={{ color: "var(--text)" }}>
          {Object.entries(cfg.currencies || {}).map(([code, s]) => (
            <option key={code} value={code}>{code} ({s})</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {cfg.tiers.map((t, i) => (
          <div key={t.id} data-testid={`pricing-tier-${t.id}`} className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }}>
            <span className="text-xs uppercase tracking-wider mono text-primary-c">{t.id}</span>
            <input data-testid={`pricing-name-${t.id}`} value={t.name} onChange={(e) => setTier(i, { name: e.target.value })}
              className={`${fieldClass} mt-2 font-display font-bold`} style={{ color: "var(--text)" }} />
            <div className="flex items-center gap-2 mt-3">
              <span className="font-display font-black text-2xl">{sym}</span>
              <input data-testid={`pricing-price-${t.id}`} type="number" min="0" value={t.price}
                onChange={(e) => setTier(i, { price: e.target.value })} className={fieldClass} style={{ color: "var(--text)" }} />
              <input data-testid={`pricing-unit-${t.id}`} value={t.unit} onChange={(e) => setTier(i, { unit: e.target.value })}
                className={`${fieldClass} max-w-[110px]`} style={{ color: "var(--text-2)" }} />
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl glow-border p-5 flex items-center justify-between gap-4 flex-wrap" style={{ background: "var(--surface)" }}>
        <div>
          <label className="text-xs uppercase tracking-wider mono" style={{ color: "var(--text-2)" }}>Default Event Fee</label>
          <div className="flex items-center gap-2 mt-1">
            <span className="font-display font-black text-xl">{sym}</span>
            <input data-testid="pricing-event-fee" type="number" min="0" value={cfg.event_fee}
              onChange={(e) => setCfg((c) => ({ ...c, event_fee: e.target.value }))}
              className={`${fieldClass} max-w-[140px]`} style={{ color: "var(--text)" }} />
          </div>
        </div>
        <button data-testid="pricing-save" onClick={save} disabled={busy}
          className="btn-glow rounded-full px-5 py-2.5 text-sm font-bold flex items-center gap-2 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Pricing
        </button>
      </div>
    </div>
  );
}
